import React, { useMemo, useState } from 'react'
import type { CategoryItem } from '../../../../../gateways/breadstack/categoryApis'
import { buildCategoryLookups } from '../../../utils/categoryLookups'
import { CategoryAvatar } from '../../common'
import { ExcludedCategoriesDialog } from '../dialogs/ExcludedCategoriesDialog'

interface Props {
  title: string
  subtitle: string
  excludedIds: number[]
  categories: CategoryItem[]
  categoriesLoading: boolean
  saving: boolean
  onChange: (nextIds: number[]) => Promise<void> | void
}

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  borderBottom: '1px solid #E8ECF0',
}

const iconBadgeStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 10,
  background: '#FEE2E2',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}

const titleStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: '#2C3E50',
  margin: 0,
}

const subtitleStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#7F8C8D',
  margin: '2px 0 0',
  maxWidth: 520,
}

const badgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 10px',
  fontSize: 11,
  fontWeight: 600,
  color: '#475569',
  background: '#F1F5F9',
  borderRadius: 999,
}

const addBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  fontSize: 13,
  fontWeight: 600,
  color: '#fff',
  background: '#2563EB',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
}

const tableRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 120px 88px',
  gap: 8,
  alignItems: 'center',
  padding: '12px 20px',
  fontSize: 13,
  color: '#2C3E50',
  borderBottom: '1px solid #F1F5F9',
}

const tableHeaderRowStyle: React.CSSProperties = {
  ...tableRowStyle,
  padding: '10px 20px',
  fontSize: 11,
  fontWeight: 600,
  color: '#94A3B8',
  textTransform: 'uppercase',
  letterSpacing: '.06em',
  background: '#FAFBFC',
}

const iconBtnStyle: React.CSSProperties = {
  padding: 6,
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: '#7F8C8D',
  borderRadius: 6,
  display: 'inline-flex',
}

const BanIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 18,
  color = '#DC2626',
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
  </svg>
)

const TrashIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
  </svg>
)

export const ExcludedCategoriesCard: React.FC<Props> = ({
  title,
  subtitle,
  excludedIds,
  categories,
  categoriesLoading,
  saving,
  onChange,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false)

  const { nameById, pathById, countById, imageById } = useMemo(
    () => buildCategoryLookups(categories),
    [categories],
  )

  const handleAdd = async (ids: number[]) => {
    const merged = Array.from(new Set([...excludedIds, ...ids]))
    await onChange(merged)
    setDialogOpen(false)
  }

  const handleRemove = async (id: number) => {
    if (saving) return
    await onChange(excludedIds.filter((x) => x !== id))
  }

  const countLabel = `${excludedIds.length} excluded`

  return (
    <>
      <div
        style={{
          background: '#fff',
          border: '1px solid #E8ECF0',
          borderRadius: 8,
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          overflow: 'hidden',
        }}
      >
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={iconBadgeStyle}>
              <BanIcon />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h3 style={titleStyle}>{title}</h3>
                <span style={badgeStyle}>{countLabel}</span>
              </div>
              <p style={subtitleStyle}>{subtitle}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            style={addBtnStyle}
          >
            + Add Categories
          </button>
        </div>

        {excludedIds.length === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '48px 16px',
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: '#FEF2F2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 16,
              }}
            >
              <BanIcon size={24} color="#DC2626" />
            </div>
            <h4
              style={{
                fontSize: 13.5,
                fontWeight: 600,
                color: '#2C3E50',
                margin: '0 0 4px',
              }}
            >
              No excluded categories
            </h4>
            <p
              style={{
                fontSize: 12,
                color: '#7F8C8D',
                textAlign: 'center',
                margin: '0 0 16px',
                maxWidth: 340,
                lineHeight: 1.5,
              }}
            >
              All product categories are available. Add a category to hide its
              products from this fulfillment method.
            </p>
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 14px',
                fontSize: 12,
                fontWeight: 500,
                color: '#FFFFFF',
                background: '#2563EB',
                border: '1px solid #2563EB',
                borderRadius: 8,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              + Add Categories
            </button>
          </div>
        ) : (
          <>
            <div style={tableHeaderRowStyle}>
              <div>Category</div>
              <div>Products</div>
              <div />
            </div>
            {excludedIds.map((id) => (
              <div key={id} style={tableRowStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <CategoryAvatar
                    name={nameById.get(id) ?? `#${id}`}
                    image={imageById.get(id)}
                  />
                  <span style={{ fontWeight: 500 }}>
                    {pathById.get(id) ?? nameById.get(id) ?? `#${id}`}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#5A6B7B' }}>
                  {categoriesLoading && !nameById.has(id)
                    ? '—'
                    : (countById.get(id) ?? 0)}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => handleRemove(id)}
                    disabled={saving}
                    style={{ ...iconBtnStyle, opacity: saving ? 0.4 : 1 }}
                    aria-label="Remove category"
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      <ExcludedCategoriesDialog
        open={dialogOpen}
        categories={categories}
        categoriesLoading={categoriesLoading}
        excludedIds={excludedIds}
        onAdd={handleAdd}
        onClose={() => setDialogOpen(false)}
        saving={saving}
      />
    </>
  )
}
